/**
 * Pilotage Planner — pure view helpers for the plans list screen. Kept separate
 * from the model so they can be unit-tested with no DOM: a tiny chart-area
 * thumbnail projected from a plan's geometry, a one-line element summary, and a
 * readable last-edited date. The list renders these; nothing here touches React.
 */
import {
  planBounds,
  type PilotagePlanDoc,
  type LonLat,
} from './plannerModel';

export interface PlanThumbnail {
  width: number;
  height: number;
  /** Polylines to stroke (route, followed contours, freehand), in SVG pixels. */
  strokes: Array<Array<[number, number]>>;
  /** Point markers (waypoints, sticky notes) in SVG pixels. */
  dots: Array<[number, number]>;
}

/**
 * Project a plan's geometry into a small fixed SVG box for the list thumbnail.
 * Equirectangular with a cos(lat) east scale so the shape isn't stretched, fitted
 * to the box preserving aspect and centred, north up. Returns null for an empty
 * plan (nothing to draw), so the caller can show a placeholder instead.
 */
export function planThumbnail(
  doc: PilotagePlanDoc,
  width = 88,
  height = 60,
  pad = 6,
): PlanThumbnail | null {
  const bounds = doc.bounds ?? planBounds(doc);
  if (!bounds) return null;
  const [minLon, minLat, maxLon, maxLat] = bounds;
  const midLat = (minLat + maxLat) / 2;
  const kx = Math.cos((midLat * Math.PI) / 180) || 1e-6;

  // Local planar frame: x east (scaled by cos lat), y north.
  const spanX = Math.max((maxLon - minLon) * kx, 1e-9);
  const spanY = Math.max(maxLat - minLat, 1e-9);
  const innerW = Math.max(width - 2 * pad, 1);
  const innerH = Math.max(height - 2 * pad, 1);
  const scale = Math.min(innerW / spanX, innerH / spanY);
  const drawnW = spanX * scale;
  const drawnH = spanY * scale;
  const offX = pad + (innerW - drawnW) / 2;
  const offY = pad + (innerH - drawnH) / 2;

  const project = ([lon, lat]: LonLat): [number, number] => [
    offX + (lon - minLon) * kx * scale,
    // SVG y grows downward, so flip: the northernmost point sits at the top.
    offY + (drawnH - (lat - minLat) * scale),
  ];

  const strokes: Array<Array<[number, number]>> = [];
  if (doc.route.waypoints.length >= 2) {
    strokes.push(doc.route.waypoints.map((w) => project(w.position)));
  }
  for (const cf of doc.contourFollows) if (cf.path.length >= 2) strokes.push(cf.path.map(project));
  for (const cd of doc.clearingDepths) if (cd.path.length >= 2) strokes.push(cd.path.map(project));
  for (const f of doc.freehand) if (f.points.length >= 2) strokes.push(f.points.map(project));

  const dots: Array<[number, number]> = [];
  for (const w of doc.route.waypoints) dots.push(project(w.position));
  for (const n of doc.stickyNotes) dots.push(project(n.position));

  return { width, height, strokes, dots };
}

/**
 * A short one-line summary of what a plan holds, e.g. "3 legs · 2 bearings · 1
 * note", so the list row says what's in a plan at a glance. Bearings groups the
 * wheel-overs, transits, clearing and back bearings; contours groups the two
 * depth-contour interactions. Empty plans say so.
 */
export function planSummaryLine(doc: PilotagePlanDoc): string {
  const parts: string[] = [];
  const wps = doc.route.waypoints.length;
  if (wps >= 2) parts.push(`${wps - 1} ${wps - 1 === 1 ? 'leg' : 'legs'}`);
  else if (wps === 1) parts.push('1 waypoint');

  const bearings =
    doc.wheelOvers.length + doc.transits.length + doc.clearingBearings.length + doc.backBearings.length;
  if (bearings) parts.push(`${bearings} ${bearings === 1 ? 'bearing' : 'bearings'}`);

  const contours = doc.contourFollows.length + doc.clearingDepths.length;
  if (contours) parts.push(`${contours} ${contours === 1 ? 'contour' : 'contours'}`);

  if (doc.freehand.length) parts.push(`${doc.freehand.length} ${doc.freehand.length === 1 ? 'sketch' : 'sketches'}`);
  if (doc.stickyNotes.length) parts.push(`${doc.stickyNotes.length} ${doc.stickyNotes.length === 1 ? 'note' : 'notes'}`);

  return parts.length ? parts.join(' · ') : 'Empty plan';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A readable local last-edited stamp, e.g. "2 Jul 2026, 14:03". */
export function formatPlanDate(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
